/**
 * FilterToolbar — search + status chips + date range for admin list surfaces
 * (P0 kit). Generalises the search-box + verification-chip row shipped in
 * SellerListPage. Fully controlled (debounce stays caller-owned). Status chips
 * use GPill's `active` toggle for the selected look (never color-only — they
 * also carry `data-active` + `aria-pressed`). `--g-*` tokens; ≥44px targets.
 * Design: _shared/KIT-SPEC.md.
 */
import { type ReactElement } from "react";
import { Search } from "lucide-react";
import { GPill } from "@garden/ui";

export interface FilterChip {
  id: string;
  label: string;
}
export interface DateRange {
  from: string | null;
  to: string | null;
}
export interface FilterToolbarProps {
  search: string;
  onSearchChange: (value: string) => void;
  searchPlaceholder: string;
  searchAriaLabel: string;
  chips?: readonly FilterChip[];
  activeChipId?: string | null;
  onChipChange?: (id: string | null) => void;
  allChipLabel?: string;
  dateRange?: DateRange;
  onDateRangeChange?: (range: DateRange) => void;
  dateFromLabel?: string;
  dateToLabel?: string;
  testid?: string;
}

const dateInputClasses =
  "min-h-[44px] rounded-[var(--g-radius-sm)] border border-[var(--g-hairline)] bg-[var(--g-surface)] px-3 py-2 text-sm tabular-nums text-foreground focus:outline-none focus:ring-2 focus:ring-[var(--g-primary)]";

export function FilterToolbar({
  search,
  onSearchChange,
  searchPlaceholder,
  searchAriaLabel,
  chips,
  activeChipId = null,
  onChipChange,
  allChipLabel = "All",
  dateRange,
  onDateRangeChange,
  dateFromLabel,
  dateToLabel,
  testid = "filter-toolbar",
}: FilterToolbarProps): ReactElement {
  return (
    <div data-testid={testid} className="flex flex-col gap-3">
      <div className="relative w-full max-w-md">
        <Search
          size={16}
          strokeWidth={1.75}
          aria-hidden
          className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[var(--g-secondary)]"
        />
        <input
          type="search"
          data-testid={`${testid}-search`}
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder={searchPlaceholder}
          aria-label={searchAriaLabel}
          className="min-h-[44px] w-full rounded-[var(--g-radius-sm)] border border-[var(--g-hairline)] bg-[var(--g-surface)] py-2 pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--g-primary)]"
        />
      </div>

      {chips && chips.length > 0 && onChipChange ? (
        <div className="flex flex-wrap items-center gap-2" data-testid={`${testid}-chips`}>
          <button
            type="button"
            data-testid={`${testid}-chip-all`}
            data-active={activeChipId === null ? "true" : "false"}
            aria-pressed={activeChipId === null}
            onClick={() => onChipChange(null)}
            className="min-h-[44px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--g-primary)] rounded-full"
          >
            <GPill tone="primary" active={activeChipId === null}>
              {allChipLabel}
            </GPill>
          </button>
          {chips.map((chip) => (
            <button
              key={chip.id}
              type="button"
              data-testid={`${testid}-chip-${chip.id}`}
              data-active={activeChipId === chip.id ? "true" : "false"}
              aria-pressed={activeChipId === chip.id}
              onClick={() => onChipChange(chip.id)}
              className="min-h-[44px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--g-primary)] rounded-full"
            >
              <GPill tone="primary" active={activeChipId === chip.id}>
                {chip.label}
              </GPill>
            </button>
          ))}
        </div>
      ) : null}

      {dateRange && onDateRangeChange ? (
        <div className="flex flex-wrap items-center gap-3" data-testid={`${testid}-dates`}>
          <label className="flex items-center gap-2 text-sm text-[var(--g-secondary)]">
            {dateFromLabel}
            <input
              type="date"
              data-testid={`${testid}-date-from`}
              value={dateRange.from ?? ""}
              onChange={(e) => onDateRangeChange({ from: e.target.value || null, to: dateRange.to })}
              className={dateInputClasses}
            />
          </label>
          <label className="flex items-center gap-2 text-sm text-[var(--g-secondary)]">
            {dateToLabel}
            <input
              type="date"
              data-testid={`${testid}-date-to`}
              value={dateRange.to ?? ""}
              onChange={(e) => onDateRangeChange({ from: dateRange.from, to: e.target.value || null })}
              className={dateInputClasses}
            />
          </label>
        </div>
      ) : null}
    </div>
  );
}

export default FilterToolbar;
